import { lazy, StrictMode, Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import './css/arphyx.css'
import RootStore from '@/RootStore'
import { RootStoreContext } from '@/contexts/root-store-context'
import { replace, RouterProvider } from 'react-router'
import { RouterProvider as AriaRouterProvider } from 'react-aria-components'
import { createBrowserRouter } from 'react-router-dom'
import { createRoutes as createAuthRoutes } from '@/features/iam/auth/routes'
import { createRoutes as createAccountRoutes } from '@/features/account/routes'
import { createRoutes as createDocsRoutes } from '@/features/docs/routes'
import { createRoutes as createActivityRoutes } from '@/features/activity/routes'
import { createRoutes as createJobRoutes } from '@/features/jobs/routes'
import { createRoutes as createDashboardRoutes } from '@/features/dashboard/routes'
import { createRoutes as createBookkeepingRoutes } from '@/features/bookkeeping/routes'
import { createRoutes as createWaitlistRoutes } from '@/features/waitlist/routes'
import { createRoutes as createMagicLinkRoutes } from '@/features/iam/magic-link/routes'
import { createRoutes as createExternalCheckoutRoutes } from '@/features/billing/external-checkout/routes'
import { authenticated } from '@/features/iam/auth/authenticated-route'
import { LoadingPage } from '@/components/LoadingPage'
import { domAnimation, LazyMotion, MotionConfig } from 'motion/react'
import { SnackbarsContainer } from '@/features/shell/snackbars'
import { BaseChromeThemeContainer } from '@/features/shell/theming/BaseChromeThemeContainer'
import { ModalStackContextProvider } from '@/components/ModalDialog/ModalStackContext'
import { createEmailConfirmationRoutes } from '@/features/email-confirmation/routes'
import { PageBottomOffsetContextProvider } from '@/contexts/page-bottom-offset-context'
import { ScrollPaddingProvider } from '@/contexts/scroll-padding-context'
import { map } from 'rxjs'
import { links } from '@/utils/link'
import { userActive$ } from '@/utils/browser-util'
import { pageActivityStream } from '@/infra/page-activity'
import { LoadedImageUrlsCacheContextProvider } from '@/contexts/image-context'
import { DocumentTitleProvider } from '@/components/DocumentTitleProvider'
import { ChatSupportWidgetManagerProvider } from '@/features/support/components/ChatSupportSpaceManager'

// Lazy-load the error pages since they contain illustrations, we don't
// want them in the main bundle.
const RouterErrorBoundary = lazy(() =>
  import('@/features/shell/error-pages/ErrorPageContainer').then((r) => ({
    default: r.RouterErrorBoundary,
  })),
)

const TechnicalBreakPage = lazy(() =>
  import('@/features/shell/error-pages/ErrorPageContainer').then((r) => ({
    default: r.TechnicalBreakPage,
  })),
)

const ShellContainer = lazy(() => import('@/features/shell/ShellContainer'))

;(function () {
  const rootNode = document.getElementById('tf-arphyx-app')!
  const rootStore = new RootStore({
    pageActivity$: pageActivityStream(true),
    userActive$: userActive$,
    navigate: (to, opts) => void router.navigate(to, opts),
  })

  // Short-circuit and render the error page if the backends are down.
  if (window.__TECHNICAL_BREAK__) {
    ReactDOM.createRoot(rootNode).render(
      <StrictMode>
        <RootStoreContext value={rootStore}>
          <TechnicalBreakPage />
        </RootStoreContext>
      </StrictMode>,
    )
    return
  }

  const router = createBrowserRouter([
    {
      ErrorBoundary: RouterErrorBoundary,
      hydrateFallbackElement: <LoadingPage full />,
      children: [
        ...createMagicLinkRoutes(),
        ...createExternalCheckoutRoutes(
          rootStore.billing.internal.lazy((b) => b.externalCheckoutStore),
        ),
        {
          path: '/__session/:session_payload',
          loader: async () => {
            const url = await rootStore.iam.authStore.callback()

            if (!url) {
              window.location.replace(links.sessionExpired())
              return
            }

            return replace(url)
          },
          element: <></>,
        },
        ...createAuthRoutes(rootStore.iam),
        ...createEmailConfirmationRoutes(),
        authenticated(rootStore.iam, [
          {
            Component: ShellContainer,
            path: '/',
            children: [
              ...createBookkeepingRoutes({
                bookkeepingPlatformConnectionStore:
                  rootStore.bookkeeping.internal.lazy(
                    (m) => m.bookkeepingPlatformConnectionStore,
                  ),
                createSnackbarErrorHandler:
                  rootStore.shell.createSnackbarErrorHandler,
                currentWorkspaceId$: rootStore.iam.currentWorkspace$.pipe(
                  map((w) => w?.id ?? null),
                ),
              }),
              ...createDashboardRoutes(),
              ...createAccountRoutes(),
              ...createDocsRoutes(),
              ...createActivityRoutes(),
              ...createJobRoutes({
                waitForAuthResolution: rootStore.iam.waitForAuthResolution,
                jobs: rootStore.jobs,
                selectWorkspace: rootStore.iam.selectWorkspaceById,
              }),
              ...createWaitlistRoutes(),
            ],
          },
        ]),
        authenticated(rootStore.iam, [
          {
            path: '/widget',
            lazy: {
              Component: () =>
                import('@/features/jobs/job-widget/components/JobWidgetViewContainer/JobWidgetViewContainer').then(
                  (r) => r.default,
                ),
            },
          },
        ]),
      ],
    },
  ])

  ReactDOM.createRoot(rootNode).render(
    <StrictMode>
      <Suspense fallback={<LoadingPage full />}>
        {/*
        We're using the lazy-load-compatible API for Framer Motion, but we're
        loading all the features we need immediately synchronously.

        Other modules can load the expanded feature set if needed.
      */}
        <LazyMotion strict features={domAnimation}>
          <MotionConfig reducedMotion="user">
            <DocumentTitleProvider>
              <LoadedImageUrlsCacheContextProvider>
                <ScrollPaddingProvider>
                  <RootStoreContext.Provider value={rootStore}>
                    <BaseChromeThemeContainer>
                      <ModalStackContextProvider>
                        <AriaRouterProvider navigate={router.navigate}>
                          <PageBottomOffsetContextProvider>
                            <ChatSupportWidgetManagerProvider>
                              <RouterProvider router={router} />
                            </ChatSupportWidgetManagerProvider>
                            <SnackbarsContainer />
                          </PageBottomOffsetContextProvider>
                        </AriaRouterProvider>
                      </ModalStackContextProvider>
                    </BaseChromeThemeContainer>
                  </RootStoreContext.Provider>
                </ScrollPaddingProvider>
              </LoadedImageUrlsCacheContextProvider>
            </DocumentTitleProvider>
          </MotionConfig>
        </LazyMotion>
      </Suspense>
    </StrictMode>,
  )
})()
